import { useEffect, useMemo } from "react";
import { useResources } from "../../hooks/useResources";
import type { SelectOption } from "../../interfaces/componentInterfaces/dropdown";
import type { Subject } from "../../interfaces/apiInterfaces/subjects";
import { useCountriesStore } from "../../stores/countries.store";
import { useDisciplinesStore } from "../../stores/disciplines.store";
import { useFiltersStore } from "../../stores/filters.store";
import { useGradesStore } from "../../stores/grades.store";
import { useLanguagesStore } from "../../stores/languages.store";
import { useSubjectsStore } from "../../stores/subjects.store";
import SelectDropdown from "../dropdown/SelectDropdown";
import "./FilterDropdowns.scss";

interface FilterDropdownsProps {
  className?: string;
  showCountry?: boolean;
  showGrade?: boolean;
  showLanguage?: boolean;
  showSubject?: boolean;
  showDiscipline?: boolean;
}

const FilterDropdowns: React.FC<FilterDropdownsProps> = ({
  className = "",
  showCountry = true,
  showGrade = true,
  showLanguage = true,
  showSubject = true,
  showDiscipline = false,
}) => {
  const { getResourceValue } = useResources();

  // Use separate stores
  const {
    selectedCountryIsoCode,
    selectedGradeId,
    selectedLanguageIsoCode,
    selectedSubjectId,
    selectedDisciplineId,
    setSelectedCountryIsoCode,
    setSelectedGradeId,
    setSelectedLanguageIsoCode,
    setSelectedSubjectId,
    setSelectedDisciplineId,
  } = useFiltersStore();

  const {
    countries,
    isLoading: isLoadingCountries,
    load: loadCountries,
  } = useCountriesStore();

  const {
    grades,
    allGrades,
    isLoading: isLoadingGrades,
    load: loadGrades,
    filterByCountry,
  } = useGradesStore();

  const {
    languages,
    isLoading: isLoadingLanguages,
    load: loadLanguages,
  } = useLanguagesStore();

  const {
    subjects,
    allSubjects,
    isLoading: isLoadingSubjects,
    load: loadSubjects,
    filterByGradeAndLanguage,
  } = useSubjectsStore();

  const {
    disciplines,
    isLoading: isLoadingDisciplines,
    load: loadDisciplines,
  } = useDisciplinesStore();

  // When discipline is selected, other dropdowns are disabled; when any of country/grade/language/subject is selected, discipline is disabled
  const isDisciplineMode = Boolean(selectedDisciplineId);
  const isCountryGradeLanguageSubjectMode =
    Boolean(selectedCountryIsoCode) ||
    Boolean(selectedGradeId) ||
    Boolean(selectedLanguageIsoCode) ||
    Boolean(selectedSubjectId);

  // Fetch countries, grades, languages, and disciplines on mount
  useEffect(() => {
    if (showCountry) loadCountries();
    if (showGrade) loadGrades();
    if (showLanguage) loadLanguages();
    if (showDiscipline) loadDisciplines();
  }, [showCountry, showGrade, showLanguage, showDiscipline, loadCountries, loadGrades, loadLanguages, loadDisciplines]);

  // Filter grades when country changes or when allGrades is loaded
  useEffect(() => {
    if (allGrades.length > 0) {
      filterByCountry(selectedCountryIsoCode);
    }
  }, [selectedCountryIsoCode, filterByCountry, allGrades]);

  // Load subjects when grade is selected (without language filter to populate language dropdown)
  useEffect(() => {
    if (selectedGradeId) {
      // Load subjects without language filter so we can extract all available languages
      loadSubjects(selectedGradeId);
    }
  }, [selectedGradeId, loadSubjects]);

  // Filter subjects client-side when grade or language changes, or when allSubjects is updated after loading
  useEffect(() => {
    filterByGradeAndLanguage(selectedGradeId, selectedLanguageIsoCode);
  }, [selectedGradeId, selectedLanguageIsoCode, filterByGradeAndLanguage, allSubjects]);

  // Convert countries to dropdown options
  const countryOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_country", "Select Country") },
    ...(countries || []).map((country) => ({
      value: country.country_iso_code.toLowerCase(),
      label: country.country_english_name,
    })),
  ];

  // Convert grades to dropdown options with localization
  const gradeOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_grade", "Select Grade") },
    ...(grades || []).map((grade) => ({
      value: grade.grade_id,
      label: getResourceValue(grade.grade_localization_key, grade.grade_title),
    })),
  ];

  // Filter language options based on selected grade
  const languageOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_language", "Select Language") },
    ];

    // If no grade is selected, only show the placeholder
    if (!selectedGradeId) {
      return baseOptions;
    }

    // If languages haven't loaded yet, only show placeholder
    if (!languages || languages.length === 0) {
      return baseOptions;
    }

    // If subjects are still loading or haven't loaded yet, only show placeholder
    if (isLoadingSubjects || !allSubjects || allSubjects.length === 0) {
      return baseOptions;
    }

    // Get available languages for the selected grade from allSubjects
    // Filter subjects that match the selected grade to get their languages
    const subjectsForGrade = allSubjects.filter(
      (subject) => subject.grade_id === selectedGradeId && subject.language_iso_code
    );

    // Extract unique language codes from subjects for this grade
    // Filter out any undefined/null language codes
    const uniqueLanguageCodes = Array.from(
      new Set(
        subjectsForGrade
          .map((subject) => subject.language_iso_code?.toLowerCase())
          .filter((code): code is string => Boolean(code))
      )
    );

    // If no languages available for this grade, show placeholder only
    if (uniqueLanguageCodes.length === 0) {
      return baseOptions;
    }

    // Filter languages to only include those available for the selected grade
    const filteredLanguages = languages.filter((language) =>
      uniqueLanguageCodes.includes(language.language_iso_code.toLowerCase())
    );

    // Deduplicate languages by language_iso_code
    const uniqueFilteredLanguages = filteredLanguages.filter(
      (language, index, self) =>
        index === self.findIndex((l) => l.language_iso_code.toLowerCase() === language.language_iso_code.toLowerCase())
    );

    return [
      ...baseOptions,
      ...uniqueFilteredLanguages.map((language) => ({
        value: language.language_iso_code.toLowerCase(),
        label: language.language_english_name,
      })),
    ];
  }, [selectedGradeId, languages, allSubjects, isLoadingSubjects, getResourceValue]);

  // Convert subjects to dropdown options with deduplication
  const subjectOptions: SelectOption[] = useMemo(() => {
    const baseOptions: SelectOption[] = [
      { value: "", label: getResourceValue("select_subject", "Select Subject") },
    ];

    if (!subjects || subjects.length === 0) {
      return baseOptions;
    }

    // Deduplicate subjects by subject_id
    const uniqueSubjects = subjects.filter(
      (subject, index, self) =>
        index === self.findIndex((s) => s.subject_id === subject.subject_id)
    );

    return [
      ...baseOptions,
      ...uniqueSubjects.map((subject: Subject) => ({
        value: subject.subject_id,
        label: subject.subject_title,
      })),
    ];
  }, [subjects, getResourceValue]);

  // Convert disciplines to dropdown options
  const disciplineOptions: SelectOption[] = [
    { value: "", label: getResourceValue("select_discipline", "Select Discipline") },
    ...(disciplines || []).map((discipline) => ({
      value: discipline.discipline_id,
      label: discipline.discipline_title,
    })),
  ];

  const handleCountryChange = (value: string) => {
    setSelectedCountryIsoCode(value || null);
  };

  const handleGradeChange = (value: string) => {
    setSelectedGradeId(value || null);
  };

  const handleLanguageChange = (value: string) => {
    setSelectedLanguageIsoCode(value || null);
  };

  const handleSubjectChange = (value: string) => {
    setSelectedSubjectId(value || null);
  };

  const handleDisciplineChange = (value: string) => {
    setSelectedDisciplineId(value || null);
  };

  return (
    <ul className={`filter-dropdowns ${className}`}>
      {showDiscipline && (
        <li>
          <SelectDropdown
            name="discipline"
            id="discipline"
            options={disciplineOptions}
            value={selectedDisciplineId || ""}
            onChange={handleDisciplineChange}
            disabled={isLoadingDisciplines || isCountryGradeLanguageSubjectMode}
          />
        </li>
      )}
      {showCountry && (
        <li>
          <SelectDropdown
            name="country"
            id="country"
            options={countryOptions}
            value={selectedCountryIsoCode || ""}
            onChange={handleCountryChange}
            disabled={isLoadingCountries || isDisciplineMode}
          />
        </li>
      )}
      {showGrade && (
        <li>
          <SelectDropdown
            name="grade"
            id="grade"
            options={gradeOptions}
            value={selectedGradeId || ""}
            onChange={handleGradeChange}
            disabled={!selectedCountryIsoCode || isLoadingGrades || isDisciplineMode}
          />
        </li>
      )}
      {showLanguage && (
        <li>
          <SelectDropdown
            name="language"
            id="language"
            options={languageOptions}
            value={selectedLanguageIsoCode || ""}
            onChange={handleLanguageChange}
            disabled={!selectedGradeId || isLoadingLanguages || isDisciplineMode}
          />
        </li>
      )}
      {showSubject && (
        <li>
          <SelectDropdown
            name="subject"
            id="subject"
            options={subjectOptions}
            value={selectedSubjectId || ""}
            onChange={handleSubjectChange}
            disabled={!selectedLanguageIsoCode || isLoadingSubjects || isDisciplineMode}
          />
        </li>
      )}
    </ul>
  );
};

export default FilterDropdowns;
